"use client";
import { ArrowRightIcon } from "lucide-react";
import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";

const Faqs = [
  {
    id: "1",
    heading: "Creative decisions run on opinions",
    description: "Teams debate creatives without performance clarity.",
    answer: "No performance feedback loop between creatives and results.",
  },
  {
    id: "2",
    heading: "Winning creatives get buried too fast",
    description: "Teams identify winners after performance already drops.",
    answer: "Winning ads lose budget before the team even notices the dip.",
  },
  {
    id: "3",
    heading: "Every platform creates more manual work",
    description: "Manual creative scaling slows every campaign.",
    answer: "Creative scaling still depends on repetitive manual workflows.",
  },
];

const cards = [
  {
    heading: "creative review",
    desc: "Can we make this more UGC?",
    position: "top-[22%] left-[9%] -rotate-[2deg]",
    bgColor: "bg-[#FFEAAE]",
    textColor: "text-black",
    pin: true,
  },
  {
    heading: "slack thread • 47 replies",
    desc: "CTR dropped 18% this week.",
    position: "top-[17%] right-[11%] rotate-[6deg]",
    bgColor: "bg-[#D4DAFF]",
    textColor: "text-black",
    pin: false,
  },
  {
    heading: "spreadsheet",
    desc: "Which hook actually converted?",
    position: "top-[50%] left-[11%] rotate-[4deg]",
    bgColor: "bg-[#E6EEFF]",
    textColor: "text-black",
    pin: false,
  },
  {
    heading: "creative-iteration-12",
    desc: "Need TikTok versions by tonight.",
    position: "top-[46%] right-[4%] -rotate-[3deg]",
    bgColor: "bg-[#C9F1D0]",
    textColor: "text-black",
    pin: false,
  },
  {
    heading: "ads manager",
    desc: "This winner fatigue is getting expensive.",
    position:
      "bottom-[4%] lg:bottom-[9%] right-[14%] lg:right-[24%] rotate-[2deg]",
    bgColor: "bg-[#FFD7CD]",
    textColor: "text-black",
    pin: false,
  },
];
const WorkflowSection = () => {
  const [open, setOpen] = useState("1");
  const [userInteracted, setUserInteracted] = useState(false);

  const ref = useRef(null);

  const [isInViewport, setIsInViewport] = useState(false);

  const checkViewport = () => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    const visible =
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <=
        (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth);

    setIsInViewport(visible);
  };

  useEffect(() => {
    let interval = null;
    if (isInViewport && !userInteracted) {
      interval = setInterval(() => {
        setOpen((prev) => {
          const current = Number(prev) || 0;
          const next = current >= 3 ? 1 : current + 1;
          return String(next);
        });
      }, 2000);
    }

    window.addEventListener("scroll", checkViewport);
    checkViewport();

    return () => {
      window.removeEventListener("scroll", checkViewport);
      if (interval) clearInterval(interval);
    };
  }, [isInViewport, userInteracted]);

  return (
    <section className="flex flex-col lg:flex-row lg:items-start pt-12 pb-12 sm:pt-20 sm:pb-20 lg:pt-28 lg:pb-28 px-6 md:px-[40px] xl:px-20 gap-10 lg:justify-between">
      <article className="w-full lg:max-w-[600px]">
        <header className="max-w-[600px] lg:-mt-6">
          <h2
            id="section-heading"
            className="text-[24px] leading-[1.15]  sm:text-[34px] lg:text-5xl md:text-[38px] 3xl:text-[64px] 4xl:text-[80px] font-medium tracking-[-0.5px]"
          >
            Most teams are <br />
            running <span className="text-[#3232A4]"> ads </span> like it's
            2015.
          </h2>
          <p className="lg:text-[#898989] text-[#898989] text-[14px] lg:text-lg sm:text-[16px] 3xl:text-xl 4xl:text-2xl max-w-[350px] lg:max-w-[490px] mt-4 sm:mt-6 font-normal tracking-[-0.3px]">
            Creative requests. Endless revisions. Guesswork in Ads Manager.
            Teams waste weeks launching campaigns without knowing what actually
            works.
          </p>
        </header>
        <div ref={ref} className="mt-6">
          {Faqs.map((faq) => (
            <div
              key={faq.id}
              className="my-3"
              onClick={() => {
                setUserInteracted(true);
                setOpen(faq.id);
              }}
              aria-expanded={open === faq.id}
            >
              <div
                className={`${open === faq.id ? "border-[#3232A4] border-[1.5px] " : "border-[0.5px]"}  cursor-pointer lg:max-w-[550px]  3xl:max-w-[550px] relative border-[#272784] rounded-[14px] px-4 py-2 lg:py-2.5 `}
              >
                <hr className="absolute left-0 top-1/2 -translate-y-1/2 border-[1.5px] 3xl:border-[2px] rounded-xl h-[30px] 3xl:h-[45px] border-[#3E3434]" />
                <div className="flex items-center gap-4">
                  <span className=" text-[#84868C] text-[10px] lg:text-[12px] 3xl:text-[14px] 4xl:text-[16px] ">
                    0{faq.id}
                  </span>
                  <div className="flex flex-col lg:gap-1">
                    <h3 className="text-[#0F1118] text-[14px] lg:text-[16px]  3xl:text-[16px] 4xl:text:[18px] font-semibold tracking-[-0.3px]">
                      {faq.heading}
                    </h3>
                    <p className="text-[#53555C] text-[14px] lg:text-[16px] 3xl:text-[14px] 4xl:text-[16px]  font-normal tracking-[-0.3px]">
                      {faq.description}
                    </p>
                  </div>
                  <span className="ml-auto">
                    <ArrowRightIcon size={18} />
                  </span>
                </div>
              </div>
              {open === faq.id && (
                <div
                  className={` flex items-center gap-2 lg:gap-4 text-[14px] 3xl:text-[16px]  4xl:text-[18px] lg:text-sm ml-5 ${
                    open === faq.id ? "transition-all duration-300" : ""
                  }`}
                >
                  <span className="  ">
                    <svg
                      width="20"
                      height="21"
                      viewBox="0 0 20 21"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.7189 16.893L12.7189 17.393V17.393L12.7189 16.893ZM19.5972 17.2461C19.7925 17.0508 19.7925 16.7343 19.5972 16.539L16.415 13.3572C16.2197 13.162 15.9031 13.162 15.7079 13.3573C15.5127 13.5525 15.5127 13.8691 15.7079 14.0644L18.5365 16.8926L15.7083 19.7212C15.513 19.9165 15.5131 20.2331 15.7083 20.4283C15.9036 20.6236 16.2202 20.6236 16.4154 20.4283L19.5972 17.2461ZM12.7189 16.893L12.7189 17.393L19.2437 17.3926L19.2437 16.8926L19.2436 16.3926L12.7189 16.393L12.7189 16.893ZM1.37305 0.184802L0.908305 0.000368379C-2.40211 8.34203 3.74441 17.3935 12.7189 17.393L12.7189 16.893L12.7189 16.393C4.45068 16.3935 -1.21208 8.05438 1.83779 0.369236L1.37305 0.184802Z"
                        fill="#3232AA"
                      />
                    </svg>
                  </span>
                  <p className="relative mt-2 lg:mt-3 text-[#3232AA] font-kalam font-normal tracking-[-0.3px]">
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>
      </article>
      {/* Right artwork. The article itself is the positioning context:
          it has a locked aspect-ratio, scales with viewport, and every
          floating card uses % offsets so they track the artwork. */}
      <article className="relative w-full max-w-[380px] sm:max-w-[600px] lg:max-w-[450px] xl:max-w-[540px] 3xl:max-w-[700px] 4xl:max-w-[750px] aspect-[540/460] mx-auto lg:mx-0 shrink-0">
        <h3 className="absolute top-4 left-6 sm:top-6 sm:left-8 z-10 text-[#84868C] text-[10px] sm:text-[14px] font-bold font-kalam">
          YOU WORKFLOW TODAY
        </h3>

        <Image
          src="/assets/homePage/afterGenerateSection/workflow-1.svg"
          alt="afterGenerate"
          fill
          className="object-contain pointer-events-none"
        />

        <div className="font-kalam">
          {cards.map(({ heading, desc, position, bgColor, pin }, idx) => (
            <div
              key={idx}
              className={`absolute ${position} ${bgColor} xs:w-[110px] xs:h-[60px]  w-[130px] h-[70px]  sm:w-[140px] sm:h-[90px]  lg:w-[167px] 3xl:w-[200px] 3xl:h-[110px] px-3 py-2 sm:px-4 sm:py-3  rounded-sm lg:rounded-[10px] shadow-sm`}
            >
              {pin && (
                <span className="absolute -top-2.5 rotate-[20deg] right-6 z-20">
                  <svg
                    width="18"
                    height="22"
                    viewBox="0 0 18 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 13.5L9 21"
                      stroke="#8A8A8A"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                    />
                    <ellipse cx="9" cy="7" rx="7" ry="6.5" fill="#E5413B" />
                    <ellipse
                      cx="6.6"
                      cy="4.8"
                      rx="2.2"
                      ry="1.6"
                      fill="#F4928E"
                      opacity="0.85"
                    />
                  </svg>
                </span>
              )}
              <h3 className="text-[#9b9ea4] xs:text-[7px] text-[9px] sm:text-[11px] 3xl:text-[13px] font-normal tracking-wide">
                {heading}
              </h3>
              <p className="text-[#0F1118]  xs:text-[8px] text-[10px] sm:text-[12px] 3xl:text-[14px] leading-snug mt-1">
                {desc}
              </p>
            </div>
          ))}
        </div>
      </article>
    </section>
  );
};

export default WorkflowSection;
